import React, { useEffect, useMemo, useState } from "react";
import { Card, Tag, Row, Col, Space, Typography, Skeleton } from "antd";
import moment from "moment";
import { ClockCircleOutlined } from "@ant-design/icons";
import UpdateKitchenOrders from "./UpdateKitchenOrders";
import { KitchenDisplayStatus } from "../../../../configs/constants";

const { Text } = Typography;

const getRelativeTime = (date) => {
  if (!date) {
    return {
      en: "0 minutes ago",
      ar: "منذ 0 دقيقة",
    };
  }

  const createdAt = moment(date);
  const now = moment();
  const diffMinutes = Math.max(now.diff(createdAt, "minutes"), 0);

  return {
    en: `${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`,
    ar: `منذ ${diffMinutes} دقيقة`,
  };
};

const TimeInfo = ({ createdAt, isDarkMode }) => {
  const timeText = getRelativeTime(createdAt);

  return (
    <div
      style={{
        marginBottom: 12,
        padding: "10px 12px",
        borderRadius: 12,
        background: isDarkMode
          ? "var(--color-primary-shadow)"
          : "var(--color-primary-shadow)",
        border: isDarkMode
          ? "1px solid var(--color-primary-shadow)"
          : "1px solid var(--color-primary-shadow)",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          minWidth: 30,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: isDarkMode
            ? "var(--color-primary-shadow)"
            : "var(--color-primary-shadow)",
          marginTop: 2,
        }}
      >
        <ClockCircleOutlined
          style={{
            color: "var(--color-primary)",
            fontSize: 15,
          }}
        />
      </div>

      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: isDarkMode ? "var(--color-bg)" : "var(--color-heading)",
            lineHeight: 1.4,
            direction: "ltr",
            textAlign: "left",
          }}
        >
          {timeText.en}
        </div>

        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: isDarkMode ? "var(--color-border)" : "var(--color-text)",
            lineHeight: 1.5,
            direction: "rtl",
            textAlign: "right",
            marginTop: 2,
          }}
        >
          {timeText.ar}
        </div>
      </div>
    </div>
  );
};

const KitchenPreparingOrders = ({
  loading = false,
  orders = [],
  onUnauthorized = () => {},
  onCooked = () => {},
  isDarkMode = false,
}) => {
  const [, setNowTick] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setNowTick(Date.now());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const styles = useMemo(() => {
    if (isDarkMode) {
      return {
        container: {
          padding: 20,
          direction: "rtl",
          textAlign: "right",
          background: "transparent",
        },
        card: {
          height: "100%",
          borderTop: "4px solid var(--color-info-light)",
          background: "var(--color-heading)",
          border: "1px solid var(--color-primary-shadow)",
          color: "var(--color-bg)",
          boxShadow: "0 8px 24px var(--color-shadow)",
        },
        itemBorder: "1px dashed var(--color-primary-shadow)",
        subText: "var(--color-border)",
      };
    }

    return {
      container: {
        padding: 20,
        direction: "rtl",
        textAlign: "right",
      },
      card: {
        height: "100%",
        borderTop: "4px solid var(--color-info)",
      },
      itemBorder: "1px dashed var(--color-border)",
      subText: "var(--color-text)",
    };
  }, [isDarkMode]);

  const safeTags = (item) => (Array.isArray(item?.tags) ? item.tags : []);

  const SkeletonGrid = () => (
    <Row gutter={[16, 16]}>
      {Array.from({ length: 8 }).map((_, i) => (
        <Col key={i} xs={24} sm={12} md={12} lg={6} xl={6}>
          <Card className="kds-order-card" style={styles.card}>
            <Skeleton active title paragraph={{ rows: 6 }} />
          </Card>
        </Col>
      ))}
    </Row>
  );

  return (
    <div style={styles.container}>
      {loading ? (
        <SkeletonGrid />
      ) : (
        <Row gutter={[16, 16]}>
          {orders.map((order) => (
            <Col key={order.id} xs={24} sm={12} md={12} lg={6} xl={6}>
              <Card
                className="kds-order-card"
                hoverable
                style={styles.card}
                title={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      direction: "rtl",
                    }}
                  >
                    <strong
                      style={{ color: isDarkMode ? "var(--color-bg)" : undefined }}
                    >
                      {order.order_id}
                    </strong>
                    <Tag color="pink">{order?.order_type?.name || "-"}</Tag>
                  </div>
                }
              >
                <TimeInfo
                  createdAt={order.created_at}
                  isDarkMode={isDarkMode}
                />

                <div className="app-mb-16">
                  {(order.items || []).map((item) => {
                    const tags = safeTags(item);

                    return (
                      <div
                        key={item.id}
                        style={{
                          padding: "10px 0",
                          borderBottom: styles.itemBorder,
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            lineHeight: 1.2,
                            color: isDarkMode ? "var(--color-bg)" : undefined,
                          }}
                        >
                          <span dir="ltr">
                            {item.quantity} x{" "}
                            <bdi dir="auto">{item.name || "-"}</bdi>
                          </span>
                        </div>

                        <div className="app-text-xs">
                          <Text style={{ color: styles.subText }}>
                            {item.name_en ? item.name_en : ""}
                          </Text>
                        </div>

                        {tags.length > 0 && (
                          <div className="app-mt-8">
                            <Space size={[6, 6]} wrap>
                              {tags.map((t) => (
                                <Tag key={t.id} color="geekblue">
                                  <span style={{ fontWeight: 600 }}>
                                    {t.name_ar || "-"}
                                  </span>
                                  {t.name && <span> / {t.name}</span>}
                                </Tag>
                              ))}
                            </Space>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <UpdateKitchenOrders
                  order_id={order.id}
                  status={KitchenDisplayStatus.Cooked}
                  title="تم الطهي • Mark Cooked"
                  className="btn-primary"
                  onUnauthorized={onUnauthorized}
                  onSuccess={() => onCooked(order.id)}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default KitchenPreparingOrders;
